import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import DashboardShell from "../components/DashboardShell";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { getSocket } from "../lib/socket";
import { Users, MessageSquare, LogOut, Shield, Trash2, ArrowLeft, Send } from "lucide-react";

const TABS = ["Wall", "Chat", "Members", "About"];

export default function GroupDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Wall");
  const [group, setGroup] = useState(null);
  const [posts, setPosts] = useState([]);
  const [members, setMembers] = useState([]);
  const [text, setText] = useState("");
  const [chatLog, setChatLog] = useState([]);
  const [chatText, setChatText] = useState("");
  const chatEnd = useRef(null);
  const socketRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/groups/${id}`);
      setGroup(data);
      if (!data.restricted) {
        const [pr, mr] = await Promise.all([
          api.get(`/groups/${id}/posts`).catch(() => ({ data: { items: [] } })),
          api.get(`/groups/${id}/members`).catch(() => ({ data: { items: [] } })),
        ]);
        setPosts(pr.data.items || []);
        setMembers(mr.data.items || []);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Socket.IO group chat lifecycle
  useEffect(() => {
    if (!group || group.restricted || !group.is_member) return;
    const s = getSocket();
    if (!s) return;
    socketRef.current = s;
    const onConnect = () => s.emit("join_group_chat", { group_id: id });
    if (s.connected) onConnect(); else s.on("connect", onConnect);
    const onMsg = (m) => {
      if (m.group_id !== id) return;
      setChatLog((cur) => [...cur, m]);
    };
    s.on("group_message", onMsg);
    return () => {
      try { s.emit("leave_group_chat", { group_id: id }); } catch (err) {
        console.warn("[group-chat] leave emit failed:", err?.message);
      }
      s.off("group_message", onMsg);
      s.off("connect", onConnect);
    };
  }, [group, id]);

  useEffect(() => {
    if (tab === "Chat") setTimeout(() => chatEnd.current?.scrollIntoView({ behavior: "smooth" }), 50);
  }, [chatLog, tab]);

  const post = async () => {
    if (!text.trim()) return;
    try { await api.post(`/groups/${id}/posts`, { content: text }); setText(""); toast.success("Posted to wall"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const comment = async (postId, content) => {
    if (!content?.trim()) return;
    try { await api.post(`/groups/${id}/posts/${postId}/comments`, { content }); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const sendChat = () => {
    const s = socketRef.current;
    if (!s || !chatText.trim()) return;
    s.emit("group_message", { group_id: id, content: chatText });
    setChatText("");
  };

  const toggleMembership = async () => {
    try {
      if (group.is_member) {
        await api.post(`/groups/${id}/leave`); toast("Left group");
      } else {
        await api.post(`/groups/${id}/join`);
        toast.success(group.privacy === "private" ? "Join request sent" : "Joined!");
      }
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeMember = async (uid) => {
    if (!window.confirm("Remove this member from the group?")) return;
    try { await api.delete(`/groups/${id}/members/${uid}`); toast.success("Removed"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const promote = async (uid) => {
    try { await api.post(`/groups/${id}/admins/${uid}`); toast.success("Promoted to admin"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteGroup = async () => {
    if (!window.confirm("Delete this group permanently?")) return;
    try { await api.delete(`/groups/${id}`); toast.success("Group deleted"); navigate("/groups"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!group) return <DashboardShell><div className="text-[#94a3b8] text-sm" data-testid="group-loading">Loading group…</div></DashboardShell>;

  return (
    <DashboardShell>
      <div className="space-y-5" data-testid="group-detail-page">
        <Link to="/groups" className="csc-btn-ghost !py-1.5 inline-flex items-center gap-1" data-testid="back-to-groups"><ArrowLeft size={12} /> Groups</Link>

        <div className="csc-card p-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-[#39ff14]">// Group · {group.category} · {group.privacy}</div>
            <h1 className="text-2xl lg:text-3xl font-semibold mt-1">{group.name}</h1>
            <p className="text-sm text-[#94a3b8] mt-2 max-w-2xl">{group.description}</p>
            <div className="text-[11px] font-mono-tech text-[#94a3b8] mt-2">{group.members} member{group.members === 1 ? "" : "s"}</div>
          </div>
          <div className="flex gap-2">
            <button onClick={toggleMembership} className={group.is_member ? "csc-btn-ghost" : "csc-btn-primary"} data-testid="group-membership-btn">
              {group.is_member ? <><LogOut size={12} /> Leave</> : group.privacy === "private" ? "Request to join" : "Join"}
            </button>
            {(group.is_admin || user?.role === "admin") && (
              <button onClick={deleteGroup} className="csc-btn-ghost hover:!border-red-400 hover:!text-red-400" data-testid="group-delete-btn"><Trash2 size={12} /></button>
            )}
          </div>
        </div>

        {group.restricted ? (
          <div className="csc-card p-8 text-center text-[#94a3b8]" data-testid="group-restricted">
            This is a private group. Request to join to see members and posts.
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 border-b border-[rgba(0,170,255,0.18)] pb-1">
              {TABS.map((t) => (
                <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 text-xs font-mono-tech uppercase tracking-wider border-b-2 ${tab===t?"border-[#00aaff] text-[#00aaff]":"border-transparent text-[#94a3b8] hover:text-white"}`} data-testid={`group-tab-${t.toLowerCase()}`}>{t}</button>
              ))}
            </div>

            {tab === "Wall" && (
              <div className="space-y-4" data-testid="group-wall">
                {group.is_member && (
                  <div className="csc-card p-4 space-y-2">
                    <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Share something with the group…" rows={3} className="csc-input resize-none" data-testid="group-post-input" />
                    <div className="flex justify-end"><button onClick={post} className="csc-btn-primary" data-testid="group-post-submit">Post</button></div>
                  </div>
                )}
                {posts.length === 0 ? <div className="csc-card p-6 text-center text-[#94a3b8] text-sm" data-testid="group-wall-empty">No posts yet.</div> : posts.map((p) => (
                  <PostItem key={p.id} post={p} onComment={(c) => comment(p.id, c)} canComment={group.is_member} />
                ))}
              </div>
            )}

            {tab === "Chat" && (
              <div className="csc-card p-0 overflow-hidden flex flex-col h-[60vh]" data-testid="group-chat">
                <div className="px-4 py-3 border-b border-[rgba(0,170,255,0.18)] flex items-center gap-2 text-xs font-mono-tech text-[#94a3b8]"><MessageSquare size={12} className="text-[#39ff14]" /> Live group chat · ephemeral · members only</div>
                {!group.is_member ? (
                  <div className="flex-1 flex items-center justify-center text-[#94a3b8] text-sm">Join the group to use chat.</div>
                ) : (
                  <>
                    <div className="flex-1 overflow-auto px-4 py-3 space-y-2">
                      {chatLog.length === 0 && <div className="text-xs text-[#94a3b8] text-center py-8" data-testid="chat-empty">No messages yet. Say hi 👋</div>}
                      {chatLog.map((m, i) => (
                        <div key={i} className={`max-w-[75%] ${m.from_user_id === user?.id ? "ml-auto" : ""}`}>
                          <div className="text-[10px] font-mono-tech text-[#94a3b8] mb-0.5">{m.from_name} · {new Date(m.created_at).toLocaleTimeString()}</div>
                          <div className={`px-3 py-2 rounded-lg text-sm ${m.from_user_id === user?.id ? "bg-[#00aaff]/15 border border-[#00aaff]/30" : "bg-[#0d1b2e] border border-[rgba(0,170,255,0.18)]"}`}>{m.content}</div>
                        </div>
                      ))}
                      <div ref={chatEnd} />
                    </div>
                    <div className="border-t border-[rgba(0,170,255,0.18)] p-3 flex gap-2">
                      <input value={chatText} onChange={(e) => setChatText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") sendChat(); }} placeholder="Type a message…" className="csc-input flex-1" data-testid="group-chat-input" />
                      <button onClick={sendChat} className="csc-btn-primary" data-testid="group-chat-send"><Send size={12} /></button>
                    </div>
                  </>
                )}
              </div>
            )}

            {tab === "Members" && (
              <div className="csc-card p-5" data-testid="group-members">
                <div className="flex items-center gap-2 mb-3 text-sm font-semibold"><Users size={14} className="text-[#00aaff]" /> Members ({members.length})</div>
                <div className="divide-y divide-[rgba(0,170,255,0.18)]">
                  {members.map((m) => (
                    <div key={m.id} className="flex items-center justify-between py-2.5" data-testid={`member-${m.id}`}>
                      <Link to={m.handle ? `/u/${m.handle}` : `/profile/${m.id}`} className="text-sm hover:text-[#00aaff]">{m.name}{m.is_admin && <span className="ml-2 font-mono-tech text-[10px] text-[#39ff14]">ADMIN</span>}</Link>
                      {(group.is_admin || user?.role === "admin") && m.id !== group.creator_id && (
                        <div className="flex gap-2">
                          {!m.is_admin && <button onClick={() => promote(m.id)} className="csc-btn-ghost !py-1 !px-2 !text-[10px]" data-testid={`promote-${m.id}`}><Shield size={10} /> Promote</button>}
                          <button onClick={() => removeMember(m.id)} className="csc-btn-ghost !py-1 !px-2 !text-[10px] hover:!border-red-400 hover:!text-red-400" data-testid={`remove-${m.id}`}>Remove</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tab === "About" && (
              <div className="csc-card p-6 space-y-2 text-sm" data-testid="group-about">
                <div><span className="font-mono-tech text-[10px] uppercase text-[#94a3b8]">Category</span> · {group.category}</div>
                <div><span className="font-mono-tech text-[10px] uppercase text-[#94a3b8]">Privacy</span> · {group.privacy}</div>
                <div><span className="font-mono-tech text-[10px] uppercase text-[#94a3b8]">Members</span> · {group.members}</div>
                <p className="pt-3 text-[#94a3b8]">{group.description || "No description provided."}</p>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardShell>
  );
}

function PostItem({ post, onComment, canComment }) {
  const [reply, setReply] = useState("");
  return (
    <div className="csc-card csc-overflow-safe p-4" data-testid={`wall-post-${post.id}`}>
      <div className="text-xs font-mono-tech text-[#94a3b8]">{post.author_name} · {new Date(post.created_at).toLocaleString()}</div>
      <p className="text-sm mt-1 whitespace-pre-wrap csc-break-anywhere">{post.content}</p>
      <div className="mt-3 space-y-1.5 pl-3 border-l border-[rgba(0,170,255,0.18)]">
        {(post.comments || []).map((c, i) => (
          <div key={i} className="text-xs">
            <span className="text-[#00aaff] font-mono-tech">{c.author_name}</span>{" "}
            <span className="text-[#94a3b8]">{c.content}</span>
          </div>
        ))}
      </div>
      {canComment && (
        <div className="mt-2 flex gap-2">
          <input value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { onComment(reply); setReply(""); } }} placeholder="Write a comment…" className="csc-input !py-1.5 !text-xs" data-testid={`wall-comment-input-${post.id}`} />
          <button onClick={() => { onComment(reply); setReply(""); }} className="csc-btn-ghost !py-1.5" data-testid={`wall-comment-submit-${post.id}`}>Reply</button>
        </div>
      )}
    </div>
  );
}
